/**
 * Lumnis MCP landing page content.
 *
 * Centralized copy for /mcp, mirroring the landing-content.ts convention so
 * Abu can edit copy without touching components.
 *
 * VOICE RULES (same as the v4 landing page):
 *   - No em dashes. Use a period, comma, or colon instead.
 *   - Avoid: seamless, effortless, supercharge, revolutionize, game-changing,
 *     autopilot, "AI SDR", 24/7, exclamation points.
 *   - Describe the combined release, including Person Intelligence. Publish
 *     this copy with the corresponding PI runtime and database migrations.
 *   - The complete, source-tested function directory lives in mcp-catalog.ts.
 *
 * ⚠️ PRE-PUBLISH CHECKLIST
 *   1. Confirm MCP_ENDPOINT below is the live MCP host and OAuth is enabled
 *      there. The MCP server is hosted at https://app.lumnis.ai/mcp. Change
 *      this ONE constant if the host differs.
 *   2. Paste the Loom share URL into MCP_VIDEO.media.src and flip type to
 *      'embed'. Until then the section renders a play-button placeholder.
 */

// Single source of truth for the MCP server URL. Used by hero, connect steps,
// and the closing CTA. If the host changes, change it here only.
export const MCP_ENDPOINT = 'https://app.lumnis.ai/mcp';

// ============================================================================
// §1 HERO
// ============================================================================

export const MCP_HERO = {
  eyebrow: 'Lumnis MCP',
  headlineLead: 'Your insights.',
  headlineRest: 'Your assistant.',
  subheadline:
    'Lumnis shows you which accounts are in market this week and the warmest way in. Ask your AI assistant to research a person, map an account, understand an audience, and turn the evidence into a next step in the same Lumnis workspace.',
  connectionSteps: [
    'Add the URL in your assistant’s MCP or connector settings.',
    'Sign in to Lumnis and approve access in your browser.',
    'Ask “Show my Lumnis organizations,” then choose your workspace.'
  ],
  primaryCta: 'Setup instructions',
  secondaryCta: 'Explore every function',
  compatibility: 'For clients that support remote MCP and OAuth.',
  example: {
    prompt:
      'Brief me on this person and their company before my call. Start with the research we already have.',
    steps: [
      {
        title: 'Read the person',
        detail:
          'Retrieve Person Intelligence, including sources and report status.'
      },
      {
        title: 'Map the account',
        detail: 'Read the account map, relevant people and company context.'
      },
      {
        title: 'Choose the next step',
        detail:
          'Use the evidence to prepare. Confirm before buying fresh research or starting outreach.'
      }
    ]
  }
} as const;

// ============================================================================
// §2 DEMO VIDEO
// ============================================================================

export const MCP_VIDEO = {
  eyebrow: 'Demo',
  title: 'From a question to reviewable intelligence, in one chat.',
  description:
    'Research, inspect the evidence, and continue the work without opening another dashboard. Here it is end to end, in about two minutes.',
  // Swap `type` to 'embed' and paste the Loom share URL into `src` when the
  // recording is ready. 'placeholder' renders a play-button poster state.
  media: {
    type: 'placeholder' as 'placeholder' | 'embed',
    // For Loom: the share URL, e.g. https://www.loom.com/share/<id>
    // The embed component normalizes /share/ to /embed/ automatically.
    src: '',
    posterLabel: 'MCP demo, 2:30'
  }
} as const;

// ============================================================================
// §3 WHAT LUMNIS MCP IS
// ============================================================================

export const MCP_WHAT_IS = {
  eyebrow: 'One workspace. Two ways to work.',
  headlineLead: 'People review the evidence.',
  headlineRest: 'Agents can work with it.',
  description:
    'Lumnis brings public signals, research and available CRM context together around people and companies. MCP gives your assistant access to those records and the tools to continue the work, from a first question to a reviewed campaign.',
  pillars: [
    {
      id: 'discover',
      icon: 'scan-search',
      title: 'Research with context',
      body: 'Move between a person, an account and the market around them.',
      capabilities: [
        'Person Intelligence for a profile or project shortlist',
        'Account Intelligence for a named company and its people',
        'Audience Research for themes, pain points and post ideas'
      ]
    },
    {
      id: 'decide',
      icon: 'list-checks',
      title: 'Decide from evidence',
      body: 'Read what is known, see what is missing, and refine the next question.',
      capabilities: [
        'Search results, qualification evidence and prospect timelines',
        'Saved targeting personas and campaign playbooks',
        'Existing reports before another paid research run'
      ]
    },
    {
      id: 'act',
      icon: 'send',
      title: 'Continue the work',
      body: 'Use the tools that turn a reviewed shortlist into an operational next step.',
      capabilities: [
        'Enrich contact details and connect LinkedIn or an email inbox',
        'Add people to a project or an existing campaign',
        'Stage or launch campaigns, review actions and inspect results'
      ]
    }
  ]
} as const;

// ============================================================================
// §4 HOW TO CONNECT
// ============================================================================

export const MCP_CONNECT = {
  eyebrow: 'How to connect',
  headlineLead: 'One server URL.',
  headlineRest: 'Your Lumnis account.',
  description:
    'Use a client that supports remote MCP and OAuth. Add the URL, authorize Lumnis in your browser, then choose the organization you want to work in.',
  endpointLabel: 'MCP server URL',
  steps: [
    {
      number: '1',
      title: 'Add the server',
      body: 'In your MCP client, add a new connector with the Lumnis server URL.'
    },
    {
      number: '2',
      title: 'Authorize',
      body: 'Complete Lumnis sign-in and authorization in your browser. Keep passwords and verification codes out of chat.'
    },
    {
      number: '3',
      title: 'Choose your workspace',
      body: 'Ask for your account and organizations. Select one before researching or changing its projects and campaigns.'
    }
  ],
  // Per-client tabs. Each just needs the one URL above plus OAuth.
  clients: [
    {
      id: 'claude',
      name: 'Claude',
      steps: [
        'Use the remote connector setup supported by your Claude client and plan.',
        'Add the Lumnis server URL. Claude Code can add it as an HTTP MCP server.',
        'Complete OAuth in your browser, then ask Claude to list your Lumnis organizations.'
      ]
    },
    {
      id: 'cursor',
      name: 'Cursor',
      steps: [
        'Add a remote server in your Cursor MCP configuration.',
        'Use the Lumnis server URL with HTTP transport.',
        'Complete the browser authorization flow and check that Lumnis tools appear.'
      ]
    },
    {
      id: 'other',
      name: 'Other MCP clients',
      steps: [
        'Check that your client supports remote HTTP MCP servers and OAuth.',
        'Use the URL above as the endpoint, following your client documentation.',
        'Complete browser authorization, then ask for your Lumnis account and organizations.'
      ]
    }
  ]
} as const;

// ============================================================================
// §5 THE SEARCH TYPES
// ============================================================================
//
// Lead with the differentiated ones. This is the "no tool does this" sell.

export const MCP_SEARCH_TYPES = {
  eyebrow: 'The search types',
  headlineLead: 'Six ways to find buyers,',
  headlineRest: 'in plain English.',
  description:
    'Ask for any of these by describing it. Lumnis runs the search behind the scenes and returns ranked prospects with the evidence attached.',
  types: [
    {
      id: 'competitor-engagement',
      icon: 'radio-tower',
      label: 'Competitor engagement',
      body: "People who reacted to or commented on a competitor's posts. The buyer is researching the category right now.",
      featured: true
    },
    {
      id: 'competitor-rep',
      icon: 'crosshair',
      label: 'Competitor rep engagement',
      body: "The prospects a competitor's own salespeople are personally chasing. Demand no static list can show you.",
      featured: true
    },
    {
      id: 'hiring',
      icon: 'briefcase-business',
      label: 'Hiring signals',
      body: 'Companies opening roles or building out a team, a sign a project or initiative just went active.',
      featured: false
    },
    {
      id: 'job-change',
      icon: 'user-plus',
      label: 'Job-change signals',
      body: 'Decision-makers who just stepped into a new role, with a fresh mandate and budget to evaluate.',
      featured: false
    },
    {
      id: 'post-engagement',
      icon: 'message-circle',
      label: 'Post engagement',
      body: 'People engaging with content on a topic you choose.',
      featured: false
    },
    {
      id: 'general-icp',
      icon: 'user-search',
      label: 'General ICP',
      body: 'Describe the person in a sentence. We find them.',
      featured: false
    }
  ]
} as const;

// ============================================================================
// §6 "JUST ASK" — example questions by category
// ============================================================================

export const MCP_EXAMPLES = {
  eyebrow: 'Just ask',
  headlineLead: 'Different questions,',
  headlineRest: 'same chat box.',
  description:
    'Start with a question about the work, not an API call. Your assistant can read existing records first and ask before a paid run or an action that changes your workspace.',
  categories: [
    {
      id: 'discover',
      label: 'People and account intelligence',
      icon: 'scan-search',
      prompts: [
        'Read the Person Intelligence report for this LinkedIn profile. Tell me if it is incomplete before we buy fresh research.',
        'Check our Account Intelligence for this company. If we need a new map, draft the roles and review criteria first.',
        'Find operations leaders engaging with these competitors. Show me the targeting and credit cost before running the search.',
        'Read our account monitor. Which accounts are act-now this run, what is the evidence window, and what changed at the top one since the last run?'
      ]
    },
    {
      id: 'qc',
      label: 'Audience and evidence',
      icon: 'list-checks',
      prompts: [
        'Read our latest Audience Research brief. What problems are people discussing, and which post ideas speak to them?',
        'Show the engagement evidence behind this shortlist. Separate observed behavior from your interpretation.',
        'Check which prospects already have email addresses. Ask me before enriching the missing ones, and skip phone lookup.'
      ]
    },
    {
      id: 'act',
      label: 'Playbooks and campaigns',
      icon: 'send',
      prompts: [
        'Create a playbook from this exact markdown, without rewriting it.',
        'Find the existing campaign for this project. Confirm the destination and its status before adding these people.',
        'Prepare a new campaign with this playbook and shortlist without starting it. Show me the approval settings.',
        'Show the pending campaign actions so I can review the messages before deciding what to approve.'
      ]
    },
    {
      id: 'pipeline',
      label: 'Connections and workspace',
      icon: 'layout-dashboard',
      prompts: [
        'Check my LinkedIn connection. If I need to reconnect, give me the browser authorization link.',
        'List my email inboxes in this organization and tell me whether sender setup is required.',
        'Show my pipeline stats, this campaign’s results and the timeline for this prospect.'
      ]
    }
  ]
} as const;

// ============================================================================
// §7 CLOSING CTA
// ============================================================================

export const MCP_FINAL_CTA = {
  headlineLead: 'Bring the intelligence.',
  headlineRest: 'Keep the conversation.',
  description:
    'Connect the people, company and audience research in Lumnis to the assistant you work with. Start with what you know. Decide what happens next.',
  buttonText: 'Connect Lumnis MCP',
  trustLine:
    'Browser-based OAuth. Organization-scoped tools. Your existing Lumnis workspace.'
} as const;
